import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import CategoryCombobox, { CATEGORY_PANEL_RESERVED_HEIGHT } from './CategoryCombobox';
import { Plus, Trash2, Check, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  getMyVenueRooms,
  createVenueRoom,
  getVenueInventory,
  createVenueInventoryItem,
  updateVenueInventoryItem,
  deleteVenueInventoryItem,
  confirmVenueInventory,
} from '../../lib/api';
import {
  INVENTORY_TEMPLATES,
  isCapacityCategory,
  capacityFieldFor,
  labelForCategory,
  modelPlaceholderFor,
} from '../../constants/inventoryTemplates';

const AMBER = 'var(--s8-amber, #C8923E)';

const EMPTY_ROW = { category: '', model: '', quantity: '1', capacity: '' };

// Build the create/update payload from a row, coercing numbers and writing the
// single capacity value into `attributes` for capacity categories only.
function buildPayload(row) {
  const payload = {
    category: row.category,
    model: row.model,
    quantity: parseInt(row.quantity, 10) || 1,
  };
  if (isCapacityCategory(row.category)) {
    const { key } = capacityFieldFor(row.category);
    payload.attributes = { [key]: parseInt(row.capacity, 10) || 0 };
  }
  return payload;
}

export default function VenueInventoryEditor() {
  const [rooms, setRooms] = useState([]);
  const [roomId, setRoomId] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmingAll, setConfirmingAll] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newRow, setNewRow] = useState(EMPTY_ROW);
  const modelRefs = useRef({});
  const newModelRef = useRef(null);
  const [openCategoryId, setOpenCategoryId] = useState(null);
  const [newCategoryOpen, setNewCategoryOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await getMyVenueRooms();
        const list = res.data || [];
        setRooms(list);
        setRoomId(list[0]?.id || null);
      } catch {
        /* no rooms yet is a valid first-run state */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const load = useCallback(async () => {
    if (!roomId) { setItems([]); return; }
    try {
      const res = await getVenueInventory(roomId);
      setItems(res.data || []);
    } catch {
      setItems([]);
    }
  }, [roomId]);

  useEffect(() => { load(); }, [load]);

  const handleAddRoom = async () => {
    const name = window.prompt('Name this room (e.g. "Kelder")');
    if (!name?.trim()) return;
    try {
      const res = await createVenueRoom({ name: name.trim() });
      setRooms((prev) => [...prev, res.data]);
      setRoomId(res.data.id);
      toast.success(`Added "${res.data.name}"`);
    } catch {
      toast.error('Could not add that room');
    }
  };

  // ── Create ──────────────────────────────────────────────────────────────
  const createItem = async (payload) => {
    if (!roomId) return false;
    setBusy(true);
    try {
      const res = await createVenueInventoryItem(roomId, payload);
      setItems((prev) => [...prev, res.data]);
      return true;
    } catch {
      toast.error('Failed to add item');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = async () => {
    if (!newRow.category || !newRow.model.trim()) return;
    const ok = await createItem(buildPayload(newRow));
    if (ok) {
      setNewRow(EMPTY_ROW);
      setShowAddForm(false);
      toast.success('Item added');
    }
  };

  const handleAddTemplate = async (template) => {
    const ok = await createItem(buildPayload({
      category: template.category,
      model: template.model,
      quantity: String(template.quantity),
      capacity: template.attributes ? String(Object.values(template.attributes)[0]) : '',
    }));
    if (ok) toast.success(`Added ${labelForCategory(template.category)}`);
  };

  // ── Edit (local state immediate, persist on blur) ───────────────────────
  const updateLocal = (id, patch) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };

  const updateLocalCapacity = (id, value) => {
    setItems((prev) => prev.map((it) => {
      if (it.id !== id) return it;
      const { key } = capacityFieldFor(it.category);
      return { ...it, attributes: { ...(it.attributes || {}), [key]: value } };
    }));
  };

  const persistItem = async (id) => {
    const item = items.find((it) => it.id === id);
    if (!item || !item.category || !String(item.model || '').trim()) return;
    const { key } = capacityFieldFor(item.category);
    const payload = buildPayload({
      category: item.category,
      model: item.model,
      quantity: item.quantity,
      capacity: item.attributes ? item.attributes[key] : '',
    });
    try {
      const res = await updateVenueInventoryItem(id, payload);
      setItems((prev) => prev.map((it) => (it.id === id ? res.data : it)));
    } catch {
      toast.error('Failed to save change');
    }
  };

  // Category changes can flip capacity/discrete, so persist immediately.
  const handleCategoryChange = async (id, category) => {
    updateLocal(id, { category });
    const item = items.find((it) => it.id === id);
    if (!item) return;
    const { key } = capacityFieldFor(category);
    const payload = buildPayload({
      category,
      model: item.model,
      quantity: item.quantity,
      capacity: item.attributes ? item.attributes[key] : '',
    });
    try {
      const res = await updateVenueInventoryItem(id, payload);
      setItems((prev) => prev.map((it) => (it.id === id ? res.data : it)));
    } catch {
      toast.error('Failed to save change');
    }
  };

  const handleConfirmItem = async (id) => {
    try {
      // Empty-body update just re-stamps last_confirmed_at server-side.
      const res = await updateVenueInventoryItem(id, {});
      setItems((prev) => prev.map((it) => (it.id === id ? res.data : it)));
      toast.success('Marked as still correct');
    } catch {
      toast.error('Failed to confirm item');
    }
  };

  const handleRemove = async (id) => {
    setBusy(true);
    try {
      await deleteVenueInventoryItem(id);
      setItems((prev) => prev.filter((it) => it.id !== id));
      toast.success('Item removed');
    } catch {
      toast.error('Failed to remove');
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmAll = async () => {
    if (!roomId) return;
    setConfirmingAll(true);
    try {
      const res = await confirmVenueInventory(roomId);
      setRooms((prev) => prev.map((r) =>
        r.id === roomId ? { ...r, inventory_confirmed_at: res.data?.inventory_confirmed_at } : r));
      toast.success('Equipment confirmed for this room');
    } catch {
      toast.error('Failed to confirm inventory');
    } finally {
      setConfirmingAll(false);
    }
  };

  const inputCls = 'h-10 bg-[#151515] border-white/[0.08] text-white text-sm';

  return (
    <div className="venue-inventory-editor space-y-5">
      {loading ? (
        <div className="h-24 rounded-lg bg-white/[0.04] animate-pulse" />
      ) : (
        <>
          {/* Which room's equipment is being edited */}
          <div className="flex flex-wrap items-center gap-2" data-testid="room-bar">
            {rooms.map((room) => {
              const active = room.id === roomId;
              return (
                <button
                  key={room.id}
                  type="button"
                  onClick={() => setRoomId(room.id)}
                  data-testid={`room-tab-${room.id}`}
                  aria-pressed={active}
                  className="rounded-full px-3.5 py-1.5 text-xs"
                  style={{
                    fontFamily: "'DM Mono', monospace",
                    background: active ? 'rgba(200, 146, 62, 0.16)' : 'transparent',
                    color: active ? '#E8C088' : 'rgba(255,255,255,0.45)',
                    border: `1px solid ${active ? 'rgba(200, 146, 62, 0.42)' : 'rgba(255,255,255,0.10)'}`,
                  }}
                >
                  {room.name}
                  {room.inventory_confirmed_at && (
                    <Check size={11} strokeWidth={3} aria-hidden
                           style={{ display: 'inline', marginLeft: 5, color: '#5A9A6B' }} />
                  )}
                </button>
              );
            })}
            <button
              type="button"
              onClick={handleAddRoom}
              data-testid="add-room"
              className="rounded-full px-3.5 py-1.5 text-xs"
              style={{
                fontFamily: "'DM Mono', monospace", background: 'transparent',
                color: 'rgba(255,255,255,0.45)', border: '1px dashed rgba(255,255,255,0.14)',
              }}
            >
              + Room
            </button>
          </div>

          {/* Existing items */}
          {items.length === 0 ? (
            <p className="text-sm text-white/40">
              No equipment yet. Add your own below, or start from a suggestion.
            </p>
          ) : (
            <div className="space-y-2">
              {items.map((item) => {
                const cap = isCapacityCategory(item.category);
                const { key, label } = capacityFieldFor(item.category);
                return (
                  <Card
                    key={item.id}
                    data-testid={`inventory-item-${item.id}`}
                    className="bg-[#141414] border-white/[0.08]"
                  >
                    <CardContent className="p-3">
                      <div className="flex flex-wrap items-end gap-3">
                        <div className="min-w-[170px] flex-1 space-y-1">
                          <Label className="text-[10px] uppercase tracking-wide text-white/40">Category</Label>
                          <CategoryCombobox
                            value={item.category}
                            onChange={(v) => handleCategoryChange(item.id, v)}
                            onAfterSelect={() => modelRefs.current[item.id]?.focus()}
                            open={openCategoryId === item.id}
                            onOpenChange={(next) => setOpenCategoryId(next ? item.id : null)}
                            placeholder="Category"
                            triggerClassName={inputCls}
                            triggerTestId={`inventory-category-${item.id}`}
                          />
                        </div>

                        <div className="min-w-[180px] flex-[2] space-y-1">
                          <Label className="text-[10px] uppercase tracking-wide text-white/40">Model</Label>
                          <Input
                            ref={(el) => { modelRefs.current[item.id] = el; }}
                            value={item.model || ''}
                            onChange={(e) => updateLocal(item.id, { model: e.target.value })}
                            onBlur={() => persistItem(item.id)}
                            className={inputCls}
                            data-testid={`inventory-model-${item.id}`}
                          />
                        </div>

                        <div className="w-20 space-y-1">
                          <Label className="text-[10px] uppercase tracking-wide text-white/40">Qty</Label>
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity ?? ''}
                            onChange={(e) => updateLocal(item.id, { quantity: e.target.value })}
                            onBlur={() => persistItem(item.id)}
                            className={inputCls}
                            data-testid={`inventory-quantity-${item.id}`}
                          />
                        </div>

                        {cap && (
                          <div className="w-28 space-y-1">
                            <Label className="text-[10px] uppercase tracking-wide text-white/40">{label}</Label>
                            <Input
                              type="number"
                              min="0"
                              value={item.attributes?.[key] ?? ''}
                              onChange={(e) => updateLocalCapacity(item.id, e.target.value)}
                              onBlur={() => persistItem(item.id)}
                              className={inputCls}
                              data-testid={`inventory-capacity-${item.id}`}
                            />
                          </div>
                        )}

                        <div className="flex items-center gap-1.5 pb-0.5">
                          <button
                            type="button"
                            onClick={() => handleConfirmItem(item.id)}
                            title="Still correct"
                            className="p-1.5 rounded hover:bg-white/10 transition-colors"
                            data-testid={`inventory-confirm-${item.id}`}
                          >
                            <Check size={15} style={{ color: AMBER }} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemove(item.id)}
                            disabled={busy}
                            title="Remove"
                            className="p-1.5 rounded hover:bg-red-500/20 transition-colors"
                            data-testid={`inventory-remove-${item.id}`}
                          >
                            <Trash2 size={15} className="text-white/40" />
                          </button>
                        </div>
                      </div>
                      {/* Reserves room for the open category panel so it pushes the
                          next card down instead of floating over it. */}
                      <div
                        aria-hidden
                        className="transition-[height] duration-150"
                        style={{ height: openCategoryId === item.id ? CATEGORY_PANEL_RESERVED_HEIGHT : 0 }}
                      />
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Seeded suggestions — keeps the editor useful when empty */}
          {items.length === 0 && (
            <div className="space-y-2" data-testid="inventory-templates">
              <p className="text-[11px] uppercase tracking-wide text-white/40">Quick add</p>
              <div className="flex flex-wrap gap-2">
                {INVENTORY_TEMPLATES.map((t) => (
                  <button
                    key={`${t.category}-${t.model}`}
                    type="button"
                    onClick={() => handleAddTemplate(t)}
                    disabled={busy}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-colors border border-white/10 text-white/70 hover:border-white/25 hover:text-white"
                    data-testid={`inventory-template-${t.category}`}
                  >
                    <Plus size={12} style={{ color: AMBER }} />
                    {labelForCategory(t.category)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Add custom item */}
          {showAddForm ? (
            <Card className="bg-[#141414] border-white/[0.08]" data-testid="inventory-add-form">
              <CardContent className="p-3">
                <div className="flex flex-wrap items-end gap-3">
                  <div className="min-w-[170px] flex-1 space-y-1">
                    <Label className="text-[10px] uppercase tracking-wide text-white/40">Category</Label>
                    <CategoryCombobox
                      value={newRow.category}
                      onChange={(v) => setNewRow({ ...newRow, category: v })}
                      onAfterSelect={() => newModelRef.current?.focus()}
                      open={newCategoryOpen}
                      onOpenChange={setNewCategoryOpen}
                      placeholder="Select category"
                      triggerClassName={inputCls}
                      triggerTestId="inventory-new-category"
                    />
                  </div>
                  <div className="min-w-[180px] flex-[2] space-y-1">
                    <Label className="text-[10px] uppercase tracking-wide text-white/40">Model</Label>
                    <Input
                      ref={newModelRef}
                      value={newRow.model}
                      onChange={(e) => setNewRow({ ...newRow, model: e.target.value })}
                      placeholder={modelPlaceholderFor(newRow.category)}
                      className={inputCls}
                      data-testid="inventory-new-model"
                    />
                  </div>
                  <div className="w-20 space-y-1">
                    <Label className="text-[10px] uppercase tracking-wide text-white/40">Qty</Label>
                    <Input
                      type="number"
                      min="1"
                      value={newRow.quantity}
                      onChange={(e) => setNewRow({ ...newRow, quantity: e.target.value })}
                      className={inputCls}
                      data-testid="inventory-new-quantity"
                    />
                  </div>
                  {isCapacityCategory(newRow.category) && (
                    <div className="w-28 space-y-1">
                      <Label className="text-[10px] uppercase tracking-wide text-white/40">
                        {capacityFieldFor(newRow.category).label}
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        value={newRow.capacity}
                        onChange={(e) => setNewRow({ ...newRow, capacity: e.target.value })}
                        className={inputCls}
                        data-testid="inventory-new-capacity"
                      />
                    </div>
                  )}
                  <Button
                    onClick={handleAdd}
                    disabled={busy || !newRow.category || !newRow.model.trim()}
                    className="h-10"
                    style={{ background: AMBER, color: '#0E1110' }}
                    data-testid="inventory-add-btn"
                  >
                    <Plus size={15} className="mr-1" /> Add
                  </Button>
                </div>
                <div
                  aria-hidden
                  className="transition-[height] duration-150"
                  style={{ height: newCategoryOpen ? CATEGORY_PANEL_RESERVED_HEIGHT : 0 }}
                />
              </CardContent>
            </Card>
          ) : (
            <button
              type="button"
              onClick={() => setShowAddForm(true)}
              className="flex items-center gap-1.5 text-sm transition-colors hover:opacity-80"
              style={{ color: AMBER }}
              data-testid="inventory-show-add-btn"
            >
              <Plus size={15} /> Add equipment
            </button>
          )}

          {/* Confirm entire inventory */}
          <div className="pt-2 border-t border-white/[0.06]">
            <Button
              variant="outline"
              onClick={handleConfirmAll}
              disabled={confirmingAll || items.length === 0}
              className="border-white/15 text-white/80 hover:bg-white/5"
              data-testid="inventory-confirm-all-btn"
            >
              <CheckCircle2 size={16} className="mr-2" style={{ color: AMBER }} />
              {confirmingAll ? 'Confirming…' : 'This list is accurate'}
            </Button>
            <p className="text-xs text-white/30 mt-2">
              Artists see the date you last confirmed. Confirm again whenever your gear changes.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
